import React from 'react';
import { View, Pressable, ScrollView } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useNav } from '../navigation/NavContext';
import { useHospitalityStore } from '../store/useHospitalityStore';
import { seatedCount, statusCount, guestsInRoom } from '../data/derive';
import { inr } from '../data/format';
import { TONE, SOFT, ICONS, STATUS, type Tone } from '../theme';
import { useTheme } from '../theme';
import { Card, CardHead, CardSub, CardBody, Chip, Button, Icon, Txt, Row, Col, Spread, Cluster, Grow, Screen } from '../components';

/**
 * Live Ops Dashboard — ported from SK Hospitality.dc.html template lines 106-228
 * and the dashVals() view-model (lines 1421-1453), rendered with React Native primitives.
 * Numbers animate via the store's eased `disp` mirror; the SUKI feed and KPIs are nudged
 * by the sim.
 */

// spark(): the prototype's inline sparkline builder (SK Hospitality.dc.html lines 1424-1430).
function spark(vals: number[]): { line: string; area: string } {
  const mn = Math.min(...vals);
  const mx = Math.max(...vals);
  const rng = mx - mn || 1;
  const pts = vals.map((v, i) => [(i / (vals.length - 1)) * 120, 28 - ((v - mn) / rng) * 24 - 2]);
  const line = 'M' + pts.map((p) => p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' L');
  const area = line + ' L120 30 L0 30 Z';
  return { line, area };
}

interface Stat {
  label: string; value: string; vsize: number; sub: string; icon: string;
  tone: Tone; live: boolean; trend: string; series: number[];
}

export default function Dashboard() {
  const t = useTheme();
  const { navigate } = useNav();
  const disp = useHospitalityStore((s) => s.disp);
  const tables = useHospitalityStore((s) => s.tables);
  const events = useHospitalityStore((s) => s.events);
  const upsellPct = useHospitalityStore((s) => s.upsellPct);
  const upsellRules = useHospitalityStore((s) => s.settings.upsellRules);
  const upsellRule = upsellRules.find((r) => r.on) ?? upsellRules[0] ?? null;

  // Trusts the live SSE-fed count (disp.occ, see useHospitalityStore's ease())
  // over this session's own `tables` array, so the hero strip and the tile below
  // agree, and both reflect seat/free actions from other terminals live.
  const occ = Math.round(disp.occ);
  const total = tables.length;
  const pctFull = total ? Math.round((occ / total) * 100) : 0;
  const guests = guestsInRoom(tables);

  // Flat sparkline at the current value — no fabricated history (real trend data
  // would need a backend time series).
  const flat = (v: number): number[] => Array(8).fill(v);
  const stats: Stat[] = [
    { label: 'Calls live', value: String(Math.round(disp.calls)), vsize: 40, sub: 'SUKI answering now', icon: 'phone', tone: 'info', live: true, trend: 'live', series: flat(Math.round(disp.calls)) },
    { label: 'Orders today', value: String(Math.round(disp.orders)), vsize: 40, sub: 'across all channels', icon: 'check', tone: 'success', live: false, trend: '—', series: flat(Math.round(disp.orders)) },
    { label: 'On the way', value: String(Math.round(disp.onway)), vsize: 40, sub: 'in kitchen / in transit', icon: 'truck', tone: 'warn', live: false, trend: '—', series: flat(Math.round(disp.onway)) },
    { label: 'Tables occupied', value: `${Math.round(disp.occ)} / ${total}`, vsize: 38, sub: `${pctFull}% full`, icon: 'grid', tone: 'accent', live: true, trend: `${pctFull}%`, series: flat(Math.round(disp.occ)) },
    { label: 'Upsell conversion', value: `${Math.round(disp.upsell)}%`, vsize: 40, sub: 'from the Catalog rule engine', icon: 'star', tone: 'orange', live: false, trend: '—', series: flat(Math.round(disp.upsell)) },
    { label: 'Revenue today', value: inr(disp.revenue), vsize: 30, sub: 'GST-correct · SK Billing', icon: 'rupee', tone: 'success', live: true, trend: 'live', series: flat(Math.round(disp.revenue / 1000)) },
  ];

  const statusCounts = [
    { label: 'Free', count: statusCount(tables, 'free'), color: STATUS.free },
    { label: 'Reserved', count: statusCount(tables, 'reserved'), color: STATUS.reserved },
    { label: 'Seated', count: statusCount(tables, 'seated'), color: STATUS.seated },
    { label: 'Bussing', count: statusCount(tables, 'dirty'), color: STATUS.dirty },
  ];

  return (
    <Screen>
      {/* hero strip */}
      <View
        style={{
          flexDirection: 'row', alignItems: 'center', gap: 20, paddingVertical: 18, paddingHorizontal: 22,
          borderRadius: 14, backgroundColor: t.colors.accent, ...t.shadows.md,
        }}
      >
        <Grow>
          <Row gap={8}>
            <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fff' }} />
            <Txt color="#fff" size="sm" weight="semibold" upper style={{ letterSpacing: 0.6, opacity: 0.9 }}>Live · dinner service</Txt>
          </Row>
          <Txt color="#fff" weight="bold" style={{ fontSize: 23, letterSpacing: -0.4, marginTop: 6 }}>{`${guests} guest${guests === 1 ? '' : 's'} in the room · ${occ}/${total} tables occupied`}</Txt>
          <Txt color="#fff" size="md" style={{ opacity: 0.85, marginTop: 2 }}>SUKI is answering, booking and upselling in real time — you supervise.</Txt>
        </Grow>
        <View style={{ alignItems: 'flex-end' }}>
          <Txt color="#fff" size="xs" upper style={{ opacity: 0.85, letterSpacing: 0.6 }}>Revenue today</Txt>
          <Txt color="#fff" mono weight="bold" style={{ fontSize: 34, lineHeight: 38 }}>{inr(disp.revenue)}</Txt>
        </View>
      </View>

      {/* stat tiles */}
      <Cluster gap={14}>
        {stats.map((s) => {
          const sp = spark(s.series);
          return (
            <Card key={s.label} style={{ flexGrow: 1, flexBasis: '30%', minWidth: 220, paddingVertical: 16, paddingHorizontal: 18 }}>
              <Spread>
                <Txt color="ink3" size="xs" weight="semibold" upper style={{ letterSpacing: 0.7 }}>{s.label}</Txt>
                <View style={{ width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: SOFT[s.tone] }}>
                  <Icon name={s.icon} size={16} strokeWidth={1.7} color={TONE[s.tone]} />
                </View>
              </Spread>
              <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10, marginTop: 10 }}>
                <Txt color="ink" mono weight="semibold" style={{ fontSize: s.vsize, lineHeight: s.vsize, letterSpacing: -0.6 }}>{s.value}</Txt>
                <View style={{ backgroundColor: SOFT[s.tone], paddingVertical: 2, paddingHorizontal: 7, borderRadius: 999 }}>
                  <Txt color={TONE[s.tone]} size="xs" weight="semibold">{s.trend}</Txt>
                </View>
              </View>
              <Svg width="100%" height={26} viewBox="0 0 120 30" preserveAspectRatio="none" style={{ marginTop: 8 }}>
                <Path d={sp.area} fill={SOFT[s.tone]} />
                <Path d={sp.line} fill="none" stroke={TONE[s.tone]} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
              </Svg>
              <Row gap={6} style={{ marginTop: 6 }}>
                {s.live && <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: t.colors.success }} />}
                <Txt color="ink3" size="sm">{s.sub}</Txt>
              </Row>
            </Card>
          );
        })}
      </Cluster>

      {/* two-column body */}
      <Row gap={16} wrap style={{ alignItems: 'flex-start' }}>
        {/* left column */}
        <Col gap={16} style={{ flexGrow: 1, flexBasis: '55%', minWidth: 320 }}>
          {/* upsell beat */}
          <Card>
            <CardHead>
              <View>
                <Txt weight="semibold">The upsell beat</Txt>
                <CardSub>Deterministic — owned by the Catalog rule engine, not guessed</CardSub>
              </View>
              <Chip variant="warn" dot>{`${Math.round(upsellPct)}% accepted today`}</Chip>
            </CardHead>
            <CardBody>
              {upsellRule ? (
                <>
                  <Row gap={14} wrap>
                    <View style={{ flexGrow: 1, flexBasis: 150, padding: 14, borderWidth: 1, borderColor: t.colors.line, borderRadius: 10, backgroundColor: t.colors.paper2 }}>
                      <Txt color="ink3" size="sm">{upsellRule.fromItem}</Txt>
                      <Txt color="ink2" mono weight="semibold" style={{ fontSize: 26, textDecorationLine: 'line-through' }}>{inr(upsellRule.fromPrice)}</Txt>
                    </View>
                    <Icon name="arrowr" size={26} color={t.colors.ink4} strokeWidth={1.7} />
                    <View style={{ flexGrow: 1, flexBasis: 150, padding: 14, borderWidth: 1, borderColor: t.colors.accentLine, borderRadius: 10, backgroundColor: t.colors.accentSoft, position: 'relative' }}>
                      <Txt color="accentInk" size="sm">{upsellRule.toItem}</Txt>
                      <Txt color="accent" mono weight="bold" style={{ fontSize: 30 }}>{inr(upsellRule.toPrice)}</Txt>
                      <Chip variant="accent" style={{ position: 'absolute', top: -11, right: 10 }}>{`+${inr(upsellRule.toPrice - upsellRule.fromPrice)}`}</Chip>
                    </View>
                  </Row>
                  <Row gap={10} style={{ marginTop: 14, paddingTop: 13, borderTopWidth: 1, borderTopColor: t.colors.line }}>
                    <Chip variant="neutral" dot>{upsellRule.name}</Chip>
                    <Txt color="ink4" size="xs" mono>{upsellRule.id}</Txt>
                    <Button size="sm" variant="accent" style={{ marginLeft: 'auto' }} onPress={() => navigate('orders')}>
                      <Txt color="#fff" size="sm" weight="medium">Open live order</Txt>
                      <Icon name="arrowr" size={13} color="#fff" strokeWidth={1.7} />
                    </Button>
                  </Row>
                </>
              ) : (
                <Txt color="ink3" size="md" center style={{ paddingVertical: 20, paddingHorizontal: 4 }}>
                  No upsell rules configured yet — add one in Settings to power the deterministic upsell beat.
                </Txt>
              )}
            </CardBody>
          </Card>

          {/* table status mini board */}
          <Card>
            <CardHead>
              <View>
                <Txt weight="semibold">Table status</Txt>
                <CardSub>Live from SK Calendar + SK Queue</CardSub>
              </View>
              <Button size="sm" variant="ghost" onPress={() => navigate('floor')}>Open floor</Button>
            </CardHead>
            <CardBody>
              <Row gap={10} wrap>
                {statusCounts.map((c) => (
                  <Pressable
                    key={c.label}
                    onPress={() => navigate('floor')}
                    style={{ flexGrow: 1, flexBasis: '22%', minWidth: 90, borderWidth: 1, borderColor: t.colors.line, backgroundColor: t.colors.card, borderRadius: 10, padding: 12 }}
                  >
                    <Row gap={7} style={{ marginBottom: 8 }}>
                      <View style={{ width: 9, height: 9, borderRadius: 3, backgroundColor: c.color }} />
                      <Txt color="ink3" size="sm">{c.label}</Txt>
                    </Row>
                    <Txt mono weight="semibold" style={{ fontSize: 24 }}>{String(c.count)}</Txt>
                  </Pressable>
                ))}
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* SUKI live feed */}
        <Card style={{ flexGrow: 1, flexBasis: '40%', minWidth: 280, maxHeight: 560, overflow: 'hidden' }}>
          <CardHead>
            <Row gap={8}>
              <Txt weight="semibold">SUKI live</Txt>
              <View style={{ width: 7, height: 7, borderRadius: 4, backgroundColor: t.colors.success }} />
            </Row>
            <CardSub>auto-updating</CardSub>
          </CardHead>
          {/* Items never auto-dismiss — this scrolls internally once it outgrows the
              card, instead of the outer Screen ScrollView, so the feed keeps every
              item (up to pushEvent's cap) reachable by scroll rather than clipping
              or growing the whole page. */}
          <ScrollView style={{ maxHeight: 480 }}>
            <Col gap={2} style={{ padding: 8 }}>
              {events.map((e, i) => (
                <Row key={`${e.t}-${i}`} gap={11} style={{ alignItems: 'flex-start', padding: 10, borderRadius: 8 }}>
                  <View style={{ width: 28, height: 28, borderRadius: 7, alignItems: 'center', justifyContent: 'center', backgroundColor: SOFT[(e.tone as Tone)] ?? SOFT.neutral }}>
                    <Icon name={ICONS[e.icon] ? e.icon : 'info'} size={14} strokeWidth={1.7} color={TONE[(e.tone as Tone)] ?? TONE.neutral} />
                  </View>
                  <Grow>
                    <Txt color="ink2" size="md" style={{ lineHeight: 18 }}>{e.text}</Txt>
                    <Txt color="ink4" size="xs" mono style={{ marginTop: 2 }}>{e.t}</Txt>
                  </Grow>
                </Row>
              ))}
            </Col>
          </ScrollView>
        </Card>
      </Row>
    </Screen>
  );
}
